/* ============================================================
   CPFS — shared page interaction layer
   ------------------------------------------------------------
   Dependency-free port of the mockups' shared behaviours
   (.reference/js/site-v3.js + scroll-top.js): header scrolled
   state, fullscreen overlay nav (with focus management),
   [data-reveal] entrances via IntersectionObserver (no GSAP),
   category-rail arrows (scoped per rail), a back-to-top button,
   and a light desktop magnetic CTA.

   Every hook is null-guarded and idempotent (re-runnable on
   `shopify:section:load` for the theme editor). Loaded once,
   globally, with `defer`. Reduced motion or no JS = the page
   renders fully visible and usable.
   ============================================================ */

(function () {
  'use strict';

  var HEADER_SCROLL_THRESHOLD = 40;
  var BACK_TO_TOP_AFTER = 600;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var backToTopBtn = null;

  /* Gate the reveal CSS: only hide [data-reveal] when we can animate. */
  if (!reduceMotion && 'IntersectionObserver' in window) {
    document.documentElement.classList.add('cpfs-motion');
  }

  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    }
  }

  /* ---------- Header: scrolled state ---------- */

  function initHeader() {
    var header = document.querySelector('.v3-header');
    if (!header || header.dataset.cpfsBound) return;
    header.dataset.cpfsBound = '1';
    var onScroll = function () {
      header.classList.toggle('is-scrolled', window.scrollY > HEADER_SCROLL_THRESHOLD);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Fullscreen overlay nav (with focus management) ---------- */

  function initOverlayNav() {
    var nav = document.querySelector('.v3-nav');
    var openBtn = document.querySelector('.v3-burger');
    var closeBtn = document.querySelector('.v3-nav__close');
    if (!nav || !openBtn || !closeBtn || nav.dataset.cpfsBound) return;
    nav.dataset.cpfsBound = '1';

    // Portal the overlay to <body>. Inside the Shopify header section group it is
    // confined to a stacking context that paints below hero content, so the open
    // menu let the hero collage chips bleed through. As a direct body child its
    // fixed z-index covers the whole page. (No effect in the standalone mockup,
    // where the overlay is already a top-level element.)
    if (nav.parentElement !== document.body) document.body.appendChild(nav);

    var focusables = function () {
      return nav.querySelectorAll('a[href], button:not([disabled])');
    };

    var onKeydown = function (e) {
      if (e.key === 'Escape') {
        setOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;
      // Trap focus within the overlay while open.
      var items = focusables();
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    function setOpen(isOpen) {
      openBtn.setAttribute('aria-expanded', String(isOpen));
      nav.setAttribute('aria-hidden', String(!isOpen));
      document.body.style.overflow = isOpen ? 'hidden' : '';
      nav.classList.toggle('is-open', isOpen);
      if (isOpen) {
        document.addEventListener('keydown', onKeydown);
        closeBtn.focus();
      } else {
        document.removeEventListener('keydown', onKeydown);
        openBtn.focus();
      }
    }

    openBtn.addEventListener('click', function () {
      setOpen(true);
    });
    closeBtn.addEventListener('click', function () {
      setOpen(false);
    });
    nav.querySelectorAll('a').forEach(function (link) {
      link.addEventListener('click', function () {
        setOpen(false);
      });
    });

    // Accordion: a parent button toggles its collapsed sub-list; only one open at a time.
    var parents = nav.querySelectorAll('.v3-nav__parent');
    parents.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var wasOpen = btn.getAttribute('aria-expanded') === 'true';
        parents.forEach(function (other) {
          other.setAttribute('aria-expanded', 'false');
          var sl = other.parentElement.querySelector('.v3-nav__sublist');
          if (sl) sl.hidden = true;
        });
        if (!wasOpen) {
          btn.setAttribute('aria-expanded', 'true');
          var sub = btn.parentElement.querySelector('.v3-nav__sublist');
          if (sub) sub.hidden = false;
        }
      });
    });
  }

  /* ---------- Rails: prev/next controls (per rail) ----------
     Binds the category rail (.v3-rail) and any other opted-in slider
     ([data-rail], e.g. the homepage shop rail). The scroll step is the width
     of the first item in the track, so it works for any card markup. */

  function initRails() {
    document.querySelectorAll('.v3-rail, [data-rail]').forEach(function (rail) {
      if (rail.dataset.cpfsBound) return;
      var viewport = rail.querySelector('.v3-rail__viewport');
      var track = rail.querySelector('.v3-rail__track');
      if (!viewport || !track) return;
      rail.dataset.cpfsBound = '1';
      var prevBtn = rail.querySelector('[data-rail-prev]');
      var nextBtn = rail.querySelector('[data-rail-next]');

      var step = function () {
        var card = track.querySelector('.v3-rail__card') || track.firstElementChild;
        if (!card) return viewport.clientWidth * 0.8;
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return card.getBoundingClientRect().width + gap;
      };
      var update = function () {
        if (!prevBtn || !nextBtn) return;
        var max = viewport.scrollWidth - viewport.clientWidth - 1;
        prevBtn.disabled = viewport.scrollLeft <= 0;
        nextBtn.disabled = viewport.scrollLeft >= max;
      };
      if (prevBtn)
        prevBtn.addEventListener('click', function () {
          viewport.scrollBy({ left: -step(), behavior: reduceMotion ? 'auto' : 'smooth' });
        });
      if (nextBtn)
        nextBtn.addEventListener('click', function () {
          viewport.scrollBy({ left: step(), behavior: reduceMotion ? 'auto' : 'smooth' });
        });
      viewport.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      window.addEventListener('load', update);
      update();
      requestAnimationFrame(update);
    });
  }

  /* ---------- Reveal immediately on load (hero fade-in) ---------- */

  // Elements inside a [data-reveal-now] scope fade in on initial page load rather
  // than waiting for a scroll trigger — used by the recipe + product heroes, whose
  // CTA/content must never sit hidden above the fold. Runs before initReveals() so
  // the scroll observer skips anything already handled here. Pure opacity (the CSS
  // drops the transform for these), so a fade, not a slide.
  function initLoadReveals() {
    var scopes = document.querySelectorAll('[data-reveal-now]');
    if (!scopes.length) return;
    var targets = [];
    scopes.forEach(function (scope) {
      if (scope.hasAttribute('data-reveal') && !scope.hasAttribute('data-reveal-bound')) {
        targets.push(scope);
      }
      scope.querySelectorAll('[data-reveal]:not([data-reveal-bound])').forEach(function (el) {
        targets.push(el);
      });
    });
    if (!targets.length) return;
    // Mark bound so initReveals()'s observer never re-touches them.
    targets.forEach(function (el) {
      el.setAttribute('data-reveal-bound', '1');
    });
    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }
    var reveal = function () {
      targets.forEach(function (el) {
        el.classList.add('is-revealed');
      });
    };
    // Reveal a couple of frames later so the hidden initial state paints first and
    // the opacity transition actually plays (a fade on load, not an instant pop).
    requestAnimationFrame(function () {
      requestAnimationFrame(reveal);
    });
    // Safety net: rAF is paused while a tab is backgrounded, which would otherwise
    // leave an above-the-fold hero stuck invisible. This timeout still fires (idempotent
    // with the rAF path) so the hero can never stay hidden.
    setTimeout(reveal, 200);
  }

  /* ---------- Reveal on scroll (IntersectionObserver) ---------- */

  var revealObserver = null;
  function initReveals() {
    var els = document.querySelectorAll('[data-reveal]:not([data-reveal-bound])');
    if (!els.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.setAttribute('data-reveal-bound', '1');
        el.classList.add('is-revealed');
      });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries, obs) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-revealed');
              obs.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.1 },
      );
    }
    els.forEach(function (el) {
      el.setAttribute('data-reveal-bound', '1');
      revealObserver.observe(el);
    });
  }

  /* ---------- Back to top ---------- */

  function initBackToTop() {
    if (backToTopBtn) return;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'scroll-top';
    btn.setAttribute('aria-label', 'Back to top');
    btn.innerHTML = '<span class="material-symbols-outlined" aria-hidden="true">arrow_upward</span>';
    document.body.appendChild(btn);
    backToTopBtn = btn;

    var update = function () {
      btn.classList.toggle('is-visible', window.scrollY > BACK_TO_TOP_AFTER);
    };
    window.addEventListener('scroll', update, { passive: true });
    update();
    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
  }

  /* ---------- Light magnetic CTA (desktop, motion-safe) ---------- */

  function initMagnetic() {
    if (reduceMotion || !window.matchMedia('(min-width: 992px)').matches) return;
    document.querySelectorAll('[data-magnetic]:not([data-magnetic-bound])').forEach(function (magnet) {
      magnet.setAttribute('data-magnetic-bound', '1');
      var strength = 0.3;
      magnet.addEventListener('mousemove', function (e) {
        var rect = magnet.getBoundingClientRect();
        var x = (e.clientX - rect.left - rect.width / 2) * strength;
        var y = (e.clientY - rect.top - rect.height / 2) * strength;
        magnet.style.transform = 'translate(' + x + 'px,' + y + 'px)';
      });
      magnet.addEventListener('mouseleave', function () {
        magnet.style.transform = '';
      });
    });
  }

  /* ---------- Audiences: tap/hover to expand ---------- */

  function initAudiences() {
    document.querySelectorAll('.v3-audiences__row').forEach(function (row) {
      if (row.dataset.cpfsBound) return;
      row.dataset.cpfsBound = '1';
      var cards = row.querySelectorAll('.v3-audience');
      cards.forEach(function (card) {
        var activate = function () {
          cards.forEach(function (c) {
            c.classList.remove('is-active');
          });
          card.classList.add('is-active');
        };
        card.addEventListener('mouseenter', activate);
        card.addEventListener('focus', activate);
      });
    });
  }

  /* ---------- Stat counters: count 0 -> data-count once in view ---------- */

  function initCounters() {
    document.querySelectorAll('.v3-stat__value[data-count]:not([data-count-bound])').forEach(function (el) {
      el.setAttribute('data-count-bound', '1');
      var target = parseInt(el.getAttribute('data-count'), 10);
      if (isNaN(target)) return;
      var textNode = el.firstChild; // number text node; any <sup> suffix follows it
      var write = function (n) {
        if (textNode) textNode.textContent = String(n);
      };
      if (reduceMotion || !('IntersectionObserver' in window)) {
        write(target);
        return;
      }
      write(0);
      var DURATION = 1600;
      var run = function () {
        var start = null,
          finished = false;
        var tick = function (ts) {
          if (start === null) start = ts;
          var t = Math.min((ts - start) / DURATION, 1);
          write(Math.round(t * (2 - t) * target)); // easeOutQuad
          if (t < 1) {
            requestAnimationFrame(tick);
          } else {
            finished = true;
          }
        };
        requestAnimationFrame(tick);
        // Safety: guarantee the final value even if rAF is throttled/paused
        // (e.g. the tab is backgrounded mid-count), so a number never sticks partway.
        setTimeout(function () {
          if (!finished) write(target);
        }, DURATION + 800);
      };
      var obs = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              obs.unobserve(entry.target);
              run();
            }
          });
        },
        { threshold: 0.4, rootMargin: '0px 0px -10% 0px' },
      );
      obs.observe(el);
    });
  }

  /* ---------- Promo banner: auto-rotating slider (+ dots, pause on hover) ---------- */

  function initBanner() {
    document.querySelectorAll('[data-banner-track]:not([data-banner-bound])').forEach(function (track) {
      track.setAttribute('data-banner-bound', '1');
      var count = track.children.length;
      if (count < 1) return;
      var section = track.closest('.v3-banner');
      var dotsWrap = section ? section.querySelector('[data-banner-dots]') : null;
      var dots = dotsWrap ? dotsWrap.querySelectorAll('.v3-banner__dot') : [];
      var ROTATE_MS = 6000;
      var index = 0,
        timer = null;
      var render = function () {
        track.style.transform = 'translateX(' + -index * 100 + '%)';
        for (var d = 0; d < dots.length; d++) dots[d].classList.toggle('is-active', d === index);
      };
      var go = function (i) {
        index = (i + count) % count;
        render();
      };
      var stop = function () {
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      };
      var start = function () {
        stop();
        if (count > 1 && !reduceMotion)
          timer = setInterval(function () {
            go(index + 1);
          }, ROTATE_MS);
      };
      for (var d = 0; d < dots.length; d++)
        (function (i) {
          dots[i].addEventListener('click', function () {
            go(i);
            start();
          });
        })(d);
      if (section) {
        section.addEventListener('mouseenter', stop);
        section.addEventListener('mouseleave', start);
      }
      render();
      start();
    });
  }

  /* ---------- Hero: rotating audience word (in the lede) ---------- */

  function initHeroSwap() {
    var swap = document.querySelector('[data-hero-swap]');
    if (!swap || swap.dataset.cpfsBound) return;
    swap.dataset.cpfsBound = '1';
    var inner = swap.querySelector('span');
    var words = (swap.getAttribute('data-words') || '')
      .split(',')
      .map(function (w) {
        return w.trim();
      })
      .filter(Boolean);
    if (!inner || reduceMotion || words.length < 2) return;
    var i = 0;
    setInterval(function () {
      inner.style.transform = 'translateY(-115%)';
      inner.style.opacity = '0';
      setTimeout(function () {
        i = (i + 1) % words.length;
        inner.textContent = words[i];
        inner.style.transition = 'none';
        inner.style.transform = 'translateY(115%)';
        void inner.offsetWidth; // reflow so the reset isn't animated
        inner.style.transition = '';
        inner.style.transform = 'translateY(0)';
        inner.style.opacity = '1';
      }, 380);
    }, 2600);
  }

  function initAll() {
    initHeader();
    initOverlayNav();
    initRails();
    initLoadReveals();
    initReveals();
    initBackToTop();
    initMagnetic();
    initAudiences();
    initCounters();
    initBanner();
    initHeroSwap();
  }

  ready(initAll);

  /* Theme editor: re-init idempotently when a section re-renders. */
  document.addEventListener('shopify:section:load', initAll);
})();
